import React, { useRef, useEffect } from 'react';
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, Row, Col } from 'reactstrap';

export interface ConfirmModalData {
    title: string;
    icon: string;
    iconColor: string;
    confirmText: string;
    confirmIcon?: string;
    sections: ConfirmModalSection[];
    warningMessage: string;
}

export interface ConfirmModalSection {
    title: string;
    icon: string;
    fields: ConfirmModalField[];
}

export interface ConfirmModalField {
    label: string;
    value: string;
    bold?: boolean;
    color?: string;
    size?: string;
}

interface GenericConfirmModalProps {
    isOpen: boolean;
    onClose: () => void;
    onConfirm: () => void;
    data: ConfirmModalData;
    isSubmitting?: boolean;
}

const ConfirmModal: React.FC<GenericConfirmModalProps> = ({
    isOpen,
    onClose,
    onConfirm,
    data,
    isSubmitting = false
}) => {
    const confirmBtnRef = useRef<HTMLButtonElement>(null);

    useEffect(() => {
        if (isOpen) {
            // Pequeño delay para que el modal termine de animarse antes del focus
            const timer = setTimeout(() => {
                confirmBtnRef.current?.focus();
            }, 150);
            return () => clearTimeout(timer);
        }
    }, [isOpen]);

    return (
        <Modal isOpen={isOpen} toggle={onClose} centered size="md" scrollable>
            <ModalHeader toggle={onClose}>
                {data.title}
            </ModalHeader>
            <ModalBody className="p-4">
                <div className="text-center mb-4">
                    <div className="avatar-sm mx-auto mb-3">
                        <div className={`avatar-title bg-soft-${data.iconColor} text-white rounded-circle fs-2`}>
                            <i className={data.icon}></i>
                        </div>
                    </div>
                    <h5 className="modal-title">¿Está seguro de generar este documento?</h5>
                    <p className="text-muted">Por favor, revise el resumen antes de confirmar</p>
                </div>

                {data.sections.map((section, index) => (
                    <div key={index} className="border rounded-3 p-3 bg-light mt-3">
                        <h6 className="text-muted mb-3 text-uppercase fw-semibold">
                            <i className={`${section.icon} me-1`}></i> {section.title}
                        </h6>
                        <Row className="g-3">
                            {section.fields.map((field, fieldIndex) => (
                                <Col key={fieldIndex} md={section.fields.length === 1 ? 12 : 4}>
                                    <div>
                                        <small className="text-muted d-block">{field.label}</small>
                                        <span
                                            className={`fw-${field.bold ? 'bold' : 'medium'} ${field.color ? `text-${field.color}` : ''} ${field.size ? `fs-${field.size}` : ''}`}
                                        >
                                            {field.value || 'No especificado'}
                                        </span>
                                    </div>
                                </Col>
                            ))}
                        </Row>
                    </div>
                ))}

                <div className="alert alert-warning mt-3 mb-0" role="alert">
                    <i className="ri-alert-line me-2"></i>
                    <strong>Importante:</strong> {data.warningMessage}
                </div>
            </ModalBody>
            <ModalFooter className="border-top">
                <Button
                    color="light"
                    onClick={onClose}
                    disabled={isSubmitting}
                    className="px-4"
                >
                    Cancelar
                </Button>
                <Button
                    innerRef={confirmBtnRef}
                    color="primary"
                    onClick={onConfirm}
                    disabled={isSubmitting}
                    className="px-4"
                >
                    {isSubmitting ? (
                        <>
                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                            Procesando...
                        </>
                    ) : (
                        <>
                            {data.confirmIcon && <i className={`${data.confirmIcon} me-1`}></i>}
                            {data.confirmText}
                        </>
                    )}
                </Button>
            </ModalFooter>
        </Modal>
    );
};

export const createRemissionGuideConfirmData = (formData: any, detailsCount: number): ConfirmModalData => ({
    title: 'Confirmar Generación de Guía de Remisión',
    icon: 'ri-truck-line',
    iconColor: 'success',
    confirmText: 'Confirmar y Generar',
    confirmIcon: 'ri-truck-line',
    sections: [
        {
            title: 'Datos del Destinatario',
            icon: 'ri-user-line',
            fields: [
                { label: 'Nombre/Razón Social', value: formData.destinatarioName },
                { label: 'Identificación', value: formData.destinatarioId }
            ]
        },
        {
            title: 'Datos del Transporte',
            icon: 'ri-truck-line',
            fields: [
                { label: 'Transportista', value: formData.carrierName, bold: true },
                { label: 'Placa Vehículo', value: formData.plate, bold: true }
            ]
        },
        {
            title: 'Resumen de la Guía',
            icon: 'ri-file-list-3-line',
            fields: [
                {
                    label: 'Número de Detalles/Productos',
                    value: `${detailsCount} productos`,
                    bold: true,
                    color: 'primary',
                    size: '5'
                }
            ]
        }
    ],
    warningMessage: 'Una vez confirmada, la guía de remisión será enviada al SRI y no podrá ser modificada.'
});

export const createBillingConfirmData = (invoiceData: any): ConfirmModalData => {
    const formatCurrency = (amount: number) => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD'
        }).format(amount);
    };

    return {
        title: 'Confirmar Generación de Factura',
        icon: 'ri-file-list-3-line',
        iconColor: 'primary',
        confirmText: 'Confirmar y Generar',
        confirmIcon: 'ri-file-list-3-line',
        sections: [
            {
                title: 'Datos del Cliente',
                icon: 'ri-user-line',
                fields: [
                    { label: 'Nombre/Razón Social', value: invoiceData.customerName },
                    { label: 'Identificación', value: invoiceData.customerId }
                ]
            },
            {
                title: 'Resumen de Montos',
                icon: 'ri-money-dollar-circle-line',
                fields: [
                    { label: 'Subtotal:', value: formatCurrency(invoiceData.subtotal), bold: true },
                    { label: 'Total IVA:', value: formatCurrency(invoiceData.totalTax), bold: true },
                    ...(invoiceData.tip > 0 ? [{ label: 'Propina:', value: formatCurrency(invoiceData.tip), bold: true }] : []),
                    { label: 'Total a Facturar:', value: formatCurrency(invoiceData.totalAmount), bold: true, color: 'primary', size: '5' }
                ]
            }
        ],
        warningMessage: 'Una vez confirmada, la factura será enviada al SRI y no podrá ser modificada.'
    };
};

export default ConfirmModal;
